// src/components/features/content/ContentApprovalWorkflow.jsx
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  NavigateNext as NextIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import {
  alpha,
  Autocomplete,
  Box,
  Breadcrumbs,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

// Import custom components
import { Button, Card } from '../../../design-system/components/core';
import { MagicLoader } from '../../../design-system';

// Import hooks
import useContentStore from '../../../stores/contentStore';
import useNotificationStore from '../../../stores/notificationStore';
import useUserStore from '../../../stores/userStore';
import { EntityStatusBadge } from '../../core';
import ContentTypeIcon from '../../ui/ContentTypeIcon';
import WorkflowStepItem from '../../ui/ApprovalWorkflowItem';



// Content Approval Workflow Component
const ContentApprovalWorkflow = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Store hooks
  const { 
    currentContent,
    isLoading,
    error,
    fetchContentById,
    clearCurrentContent,
    clearError
  } = useContentStore();
  
  const {
    initApprovalWorkflow
  } = useContentStore();
  
  const {
    users,
    fetchUsers
  } = useUserStore();
  
  const { showSuccess, showError } = useNotificationStore();
  
  // Local state
  const [steps, setSteps] = useState([]);
  const [editingStep, setEditingStep] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Load content and users on mount
  useEffect(() => {
    loadContent();
    loadUsers();
    
    return () => {
      clearCurrentContent();
    };
  }, [id]);
  
  // Initialize steps when content is loaded
  useEffect(() => {
    if (currentContent) {
      if (currentContent.approvalWorkflow && currentContent.approvalWorkflow.steps) {
        // Load existing workflow steps
        setSteps(currentContent.approvalWorkflow.steps.map(step => ({
          name: step.name,
          assignedTo: step.assignedTo
        })));
      }
    }
  }, [currentContent]);
  
  // Load content
  const loadContent = async () => {
    try {
      await fetchContentById(id);
    } catch (error) {
      showError('Failed to load content: ' + error.message);
    }
  };
  
  // Load users
  const loadUsers = async () => {
    try {
      await fetchUsers();
    } catch (error) {
      console.log('Failed to load users:', error);
    }
  };
  
  // Add new step
  const handleAddStep = () => {
    setEditingStep({
      name: `Approval Step ${steps.length + 1}`,
      assignedTo: null
    });
    setEditDialogOpen(true);
  };
  
  // Edit step
  const handleEditStep = (index) => {
    setEditingStep({ ...steps[index], index });
    setEditDialogOpen(true);
  };
  
  // Delete step
  const handleDeleteStep = (index) => {
    const updatedSteps = [...steps];
    updatedSteps.splice(index, 1);
    setSteps(updatedSteps);
  };
  
  // Move step up
  const handleMoveUp = (index) => {
    if (index === 0) return;
    
    const updatedSteps = [...steps];
    const step = updatedSteps[index];
    updatedSteps[index] = updatedSteps[index - 1];
    updatedSteps[index - 1] = step;
    setSteps(updatedSteps);
  };
  
  // Move step down
  const handleMoveDown = (index) => {
    if (index === steps.length - 1) return;
    
    const updatedSteps = [...steps];
    const step = updatedSteps[index];
    updatedSteps[index] = updatedSteps[index + 1];
    updatedSteps[index + 1] = step;
    setSteps(updatedSteps);
  };
  
  // Save edited step
  const handleSaveStep = () => {
    if (!editingStep.name || !editingStep.assignedTo) {
      showError('Step name and assigned approver are required');
      return;
    }
    
    const updatedSteps = [...steps];
    
    if (editingStep.index !== undefined) {
      // Update existing step
      updatedSteps[editingStep.index] = {
        name: editingStep.name,
        assignedTo: editingStep.assignedTo
      };
    } else {
      // Add new step
      updatedSteps.push({
        name: editingStep.name,
        assignedTo: editingStep.assignedTo
      });
    }
    
    setSteps(updatedSteps);
    setEditDialogOpen(false);
  };
  
  // Handle step drag and drop
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const updatedSteps = [...steps];
    const [movedStep] = updatedSteps.splice(result.source.index, 1);
    updatedSteps.splice(result.destination.index, 0, movedStep);
    
    setSteps(updatedSteps);
  };
  
  // Save workflow
  const handleSaveWorkflow = async () => {
    if (steps.length === 0) {
      showError('Please add at least one approval step');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await initApprovalWorkflow(id, steps);
      showSuccess('Approval workflow created successfully');
      navigate(`/content/${id}`);
    } catch (error) {
      showError('Failed to create approval workflow: ' + error.message);
      setIsSubmitting(false);
    }
  };
  
  // Get content title
  const getContentTitle = () => {
    return currentContent?.title || 'Content';
  };
  
  // Loading state
  if (isLoading && !currentContent) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <MagicLoader variant="magical" size="medium" message="Loading content..." />
      </Box>
    );
  }
  
  // Error state
  if (error && !currentContent) {
    return (
      <Card 
        level={1} 
        sx={{ p: 3, bgcolor: alpha(theme.palette.error.main, 0.1) }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Error Loading Content
        </Typography>
        <Typography variant="body1" paragraph>
          {error}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate('/content')}
        >
          Back to Content
        </Button>
      </Card>
    );
  }
  
  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link 
          component={RouterLink} 
          to="/content"
          color="inherit"
          underline="hover"
        >
          Content
        </Link>
        <Link 
          component={RouterLink} 
          to={`/content/${id}`}
          color="inherit"
          underline="hover"
        >
          {getContentTitle()}
        </Link>
        <Typography color="text.primary">
          Approval Workflow
        </Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Card 
        level={2}
        magical={true}
        sx={{ mb: 4 }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ContentTypeIcon type={currentContent?.type || 'post'} size="medium" />
                <Typography variant="h5" sx={{ ml: 1, fontWeight: 'medium' }}>
                  Setup Approval Workflow: {getContentTitle()}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EntityStatusBadge status={currentContent?.status || 'draft'} entityType="content" />
                <Typography variant="body1" sx={{ ml: 2 }}>
                  Define the approval steps required before publishing
                </Typography>
              </Box>
            </Box>
            
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate(`/content/${id}`)}
            >
              Back to Content
            </Button>
          </Box>
        </Box>
      </Card>
      
      {/* Workflow Status */}
      {currentContent?.approvalWorkflow?.steps && currentContent.approvalWorkflow.steps.length > 0 && (
        <Card level={1} sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Current Workflow Status
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoIcon sx={{ color: theme.palette.info.main, mr: 1 }} />
              <Typography variant="body2" color="textSecondary">
                This content already has an approval workflow. Creating a new workflow will replace the current one.
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="body2">
              Current Status: {' '}
              <Chip 
                label={currentContent.approvalWorkflow.isCompleted 
                  ? 'Completed' 
                  : `Step ${currentContent.approvalWorkflow.currentStep + 1} of ${currentContent.approvalWorkflow.steps.length}`
                }
                color={currentContent.approvalWorkflow.isCompleted ? 'success' : 'primary'}
                size="small"
              />
            </Typography>
          </CardContent>
        </Card>
      )}
      
      {/* Workflow Steps */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Approval Steps
          </Typography>
          
          <Button
            variant="outlined"
            
            startIcon={<AddIcon />}
            onClick={handleAddStep}
          >
            Add Step
          </Button>
        </Box>
        
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="steps">
            {(provided) => (
              <Box
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {steps.length === 0 ? (
                  <Paper sx={{ p: 3, textAlign: 'center', bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                    <InfoIcon sx={{ fontSize: 40, color: theme.palette.info.main, mb: 2 }} />
                    <Typography variant="body1" gutterBottom>
                      No approval steps defined yet
                    </Typography>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      Add steps to create an approval workflow for this content
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddStep}
                    >
                      Add First Step
                    </Button>
                  </Paper>
                ) : (
                  steps.map((step, index) => (
                    <WorkflowStepItem
                      key={index}
                      step={step}
                      index={index}
                      onEdit={handleEditStep}
                      onDelete={handleDeleteStep}
                      onMoveUp={handleMoveUp}
                      onMoveDown={handleMoveDown}
                      isFirst={index === 0}
                      isLast={index === steps.length - 1}
                    />
                  ))
                )}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
      
      {/* Workflow Information */}
      <Card level={1} sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            About Approval Workflows
          </Typography>
          
          <Typography variant="body2" paragraph>
            Approval workflows help ensure content meets your organization's standards before publishing.
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText primary="Content status will change to 'Pending Approval' when the workflow starts" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText primary="Each approver will be notified when it's their turn to review" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText primary="Content will be marked as 'Approved' when all steps are completed" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText primary="If any approver rejects the content, the workflow will stop" />
            </ListItem>
          </List>
        </CardContent>
      </Card>
      
      {/* Save Workflow */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate(`/content/${id}`)}
        >
          Cancel
        </Button>
        
        <Button
          variant="contained"
          
          startIcon={<SaveIcon />}
          onClick={handleSaveWorkflow}
          disabled={isSubmitting || steps.length === 0}
          loading={isSubmitting}
          magical={true}
        >
          {currentContent?.approvalWorkflow?.steps && currentContent.approvalWorkflow.steps.length > 0
            ? 'Update Approval Workflow'
            : 'Create Approval Workflow'
          }
        </Button>
      </Box>
      
      {/* Edit Step Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingStep?.index !== undefined ? 'Edit Step' : 'Add Step'}
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Step Name"
              value={editingStep?.name || ''}
              onChange={(e) => setEditingStep({ ...editingStep, name: e.target.value })}
              margin="normal"
              required
            />
            
            <Autocomplete
              fullWidth
              options={users}
              value={editingStep?.assignedTo || null}
              onChange={(e, newValue) => setEditingStep({ ...editingStep, assignedTo: newValue })}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assigned Approver"
                  margin="normal"
                  required
                />
              )}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveStep}
            variant="contained" 
            
            disabled={!editingStep?.name || !editingStep?.assignedTo}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContentApprovalWorkflow;